import React, { useEffect, useState } from 'react';
import { getInterpreterLanguageLabel } from '../data/interpreterLanguages';
import {
  getAttachments,
  createAttachment,
  updateAttachment,
  deleteAttachment,
  downloadAttachment,
} from '../services/attachment.service';
import AttachmentForm from '../components/AttachmentForm';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';

const AttachmentList: React.FC = () => {
  const [attachments, setAttachments] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const fetchAttachments = async () => {
    try {
      setLoadingAttachments(true);
      const res = await getAttachments();
      setAttachments(res.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingAttachments(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, []);

  const handleCreate = () => {
    setSelected(null);
    setOpen(true);
  };

  const handleEdit = (attachment: any) => {
    setSelected(attachment);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        'Hide this document? It will no longer be sent with emails but the file is kept on record.'
      )
    ) {
      try {
        await deleteAttachment(id);
        toast.success('Document hidden');
        fetchAttachments();
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Failed to hide document');
      }
    }
  };

  const handleDownload = async (id: number) => {
    try {
      await downloadAttachment(id);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setLoading(true);
      if (selected) {
        await updateAttachment(selected.id, formData);
        toast.success('Document updated successfully');
      } else {
        await createAttachment(formData);
        toast.success('Document created successfully');
      }
      setOpen(false);
      fetchAttachments();
    } catch (error: any) {
      console.error('Error saving document:', error);
      const message =
        error.response?.data?.message ||
        Object.values(error.response?.data?.errors || {})
          .flat()
          .join(' ') ||
        'Failed to save document';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant='h4' gutterBottom>
        Documents
      </Typography>
      <Button
        variant='contained'
        color='primary'
        onClick={handleCreate}
        sx={{ mb: 2 }}
      >
        Add Document
      </Button>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email Category</TableCell>
              <TableCell>Case Type</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingAttachments ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  Loading...
                </TableCell>
              </TableRow>
            ) : attachments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  No Documents found
                </TableCell>
              </TableRow>
            ) : (
              attachments.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>
                    {a.email_category ? (
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor:
                            a.email_category === 'no_conflict_email'
                              ? '#e3f2fd'
                              : a.email_category === 'interpreter'
                                ? '#fff3e0'
                                : '#f3e5f5',
                          color:
                            a.email_category === 'no_conflict_email'
                              ? '#1976d2'
                              : a.email_category === 'interpreter'
                                ? '#e65100'
                                : '#7b1fa2',
                          fontSize: '0.875rem',
                        }}
                      >
                        {a.email_category === 'no_conflict_email'
                          ? 'No Conflict Email'
                          : a.email_category === 'interpreter'
                            ? `Interpreter (${getInterpreterLanguageLabel(a.interpreter_language || 'other')})`
                            : 'Retainer Received'}
                      </span>
                    ) : (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>
                        No Category
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {a.case_type ? (
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor:
                            a.case_type === 'Plaintiff' ? '#e8f5e9' : '#fff3e0',
                          color:
                            a.case_type === 'Plaintiff' ? '#2e7d32' : '#e65100',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        {a.case_type}
                      </span>
                    ) : (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>
                        Both
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {a.case_state ? (
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        {a.case_state}
                      </span>
                    ) : (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>
                        All States
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleDownload(a.id)}
                      title='Download'
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEdit(a)} title='Edit'>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(a.id)}
                      title='Hide'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{selected ? 'Edit Document' : 'Add Document'}</DialogTitle>
        <DialogContent>
          <AttachmentForm
            key={selected?.id ?? 'new'}
            initialValues={selected || {}}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AttachmentList;
